// om-projection.ts — derive a PROJECTED-grid descriptor for Open-Meteo models
// whose `.om` files store a non-lat/lon grid (HRRR & MET Nordic = Lambert
// Conformal Conic; UKMO = Lambert Azimuthal Equal-Area).
//
// The model's `crs_wkt` (from latest.json) is a WKT2 PROJCRS string carrying the
// projection method + parameters and a USAGE BBOX. From those we build a proj4
// source-projection definition and derive the projected grid extent.
//
// GRID GEOREF (validated against live HRRR/MET-Nordic/UKMO, dx≈dy≈native res):
//   The WKT `BBOX[latMin,lonMin,latMax,lonMax]` gives the lat/lon of the grid's
//   SW corner (latMin,lonMin) and NE corner (latMax,lonMax). Projecting those:
//     (x0,y0) = proj4(SW)   (x1,y1) = proj4(NE)
//     dx = (x1 - x0) / (nx - 1)   dy = (y1 - y0) / (ny - 1)
//   Self-check: dx and dy must be ≈ equal and ≈ the model's native resolution
//   (HRRR ~3000 m, MET Nordic ~1000 m, UKMO ~2000 m).
//
// proj4js (MIT) does the actual coordinate math; this module only parses the WKT
// params and assembles the proj4 def + extent.

const SPHERE_RADIUS = 6371229 // OM models use a sphere of this radius.

// Pull a named WKT PARAMETER["<name>", <value>] out of a crs_wkt string.
function wktParam(wkt: string, name: string): number | null {
  // name match is case-insensitive and tolerant of extra whitespace.
  const re = new RegExp(
    `PARAMETER\\[\\s*"${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\s*,\\s*(-?[\\d.]+)`,
    'i'
  )
  const m = wkt.match(re)
  return m ? Number(m[1]) : null
}

function spheroid(): string {
  return `+a=${SPHERE_RADIUS} +b=${SPHERE_RADIUS} +units=m +no_defs`
}

// Build a proj4 source-projection definition string from the model's crs_wkt.
// Supports Lambert Conformal Conic (2SP) and Lambert Azimuthal Equal-Area.
// Returns null if the WKT is a plain geographic CRS or an unsupported method.
export function proj4defFromWkt(crsWkt: string): string | null {
  if (!crsWkt.startsWith('PROJCRS')) return null

  const isLcc = /Lambert Conic Conformal/i.test(crsWkt) || /Lambert Conformal Conic/i.test(crsWkt)
  const isLaea = /Lambert Azimuthal Equal-Area/i.test(crsWkt)

  if (isLcc) {
    const lat1 = wktParam(crsWkt, 'Latitude of 1st standard parallel')
    const lat2 = wktParam(crsWkt, 'Latitude of 2nd standard parallel') ?? lat1
    const lat0 = wktParam(crsWkt, 'Latitude of false origin') ?? 0
    const lon0 = wktParam(crsWkt, 'Longitude of false origin')
    if (lat1 == null || lon0 == null) return null
    return `+proj=lcc +lat_1=${lat1} +lat_2=${lat2} +lat_0=${lat0} +lon_0=${lon0} +x_0=0 +y_0=0 ${spheroid()}`
  }

  if (isLaea) {
    const lat0 = wktParam(crsWkt, 'Latitude of natural origin')
    const lon0 = wktParam(crsWkt, 'Longitude of natural origin')
    const x0 = wktParam(crsWkt, 'False easting') ?? 0
    const y0 = wktParam(crsWkt, 'False northing') ?? 0
    if (lat0 == null || lon0 == null) return null
    return `+proj=laea +lat_0=${lat0} +lon_0=${lon0} +x_0=${x0} +y_0=${y0} ${spheroid()}`
  }

  return null
}

export interface BuildProjectionResult {
  projection: OmProjectionLite
  /** Diagnostic: derived cell sizes (metres) — should be ≈ equal & ≈ native. */
  dx: number
  dy: number
}

// Local copy of the descriptor shape to avoid a circular import on the layer.
interface OmProjectionLite {
  proj4def: string
  x0: number
  y0: number
  dx: number
  dy: number
  rotatedPole?: { lonP: number; latP: number; cRotLon: number }
}

// proj4 is dynamically required so this module's pure parsers (proj4defFromWkt)
// can be unit-tested without the heavy proj4 dependency where unneeded.
import proj4 from 'proj4'
import { parseRotatedWkt, makeRotatedTransform } from './om-rotated.js'

// Build a projection descriptor for a ROTATED lat/lon grid (rotated-pole).
// The grid is regular in ROTATED lon/lat; x0/y0/dx/dy are therefore in rotated
// DEGREES (not metres). The SW/NE bbox corners are the first/last grid points
// (true lat/lon); we map them to rotated coords to recover the origin + step.
//   Self-check: dx ≈ dy ≈ the model's native increment (GEM HRDPS = 0.0225°).
// Returns null if the WKT isn't a rotated grid.
export function buildRotatedProjection(
  crsWkt: string,
  bbox: [number, number, number, number],
  nx: number,
  ny: number
): BuildProjectionResult | null {
  const params = parseRotatedWkt(crsWkt)
  if (!params) return null
  const [latMin, lonMin, latMax, lonMax] = bbox
  const rt = makeRotatedTransform(params)
  const [x0, y0] = rt.toRot(lonMin, latMin) // SW = first grid point (rotated deg)
  const [x1Raw, y1] = rt.toRot(lonMax, latMax) // NE = last grid point (rotated deg)
  // Unwrap rotated longitude so the span is the short, positive grid extent.
  const x1 = x1Raw < x0 ? x1Raw + 360 : x1Raw
  const dx = (x1 - x0) / (nx - 1)
  const dy = (y1 - y0) / (ny - 1)
  return { projection: { proj4def: '', x0, y0, dx, dy, rotatedPole: params }, dx, dy }
}

// Build the full projection descriptor for a projected model.
//   crsWkt : the model's crs_wkt (must be a supported PROJCRS)
//   bbox   : [latMin, lonMin, latMax, lonMax] (the SW / NE grid-corner lat/lon)
//   nx, ny : decoded grid dims (nx = columns = dims[1], ny = rows = dims[0])
// Returns null for unsupported / non-projected CRS.
export function buildProjection(
  crsWkt: string,
  bbox: [number, number, number, number],
  nx: number,
  ny: number
): BuildProjectionResult | null {
  const proj4def = proj4defFromWkt(crsWkt)
  if (!proj4def) return null
  const [latMin, lonMin, latMax, lonMax] = bbox
  const fwd = proj4(proj4def) // WGS84 lon/lat -> projected
  const [x0, y0] = fwd.forward([lonMin, latMin]) // SW grid corner
  const [x1, y1] = fwd.forward([lonMax, latMax]) // NE grid corner
  const dx = (x1 - x0) / (nx - 1)
  const dy = (y1 - y0) / (ny - 1)
  return { projection: { proj4def, x0, y0, dx, dy }, dx, dy }
}
